import { useEffect, useMemo, useRef, useState } from "react";
import { Search, UserPlus, TrendingUp } from "lucide-react";
import { knownLocalPlayers } from "@/lib/openPlayStore";
import api from "@/lib/api";

// Searchable player-name field. Suggests players the organizer has played with before
// (local past sessions) + the account Player Directory, with their stats. Pick via
// Arrow keys + Enter or a tap; or type a new name and create a fresh player.
export default function PlayerNameSearch({
  value, onChange, onPick, onCreate, excludeNames = [], placeholder = "Search or add a player",
  testid = "player-search", inputRef, autoFocus = false,
}) {
  const [directory, setDirectory] = useState([]);
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const boxRef = useRef(null);
  const innerRef = useRef(null);
  const ref = inputRef || innerRef;

  const local = useMemo(() => knownLocalPlayers(), []);

  useEffect(() => {
    api.get("/players/directory").then(({ data }) => setDirectory(Array.isArray(data) ? data : []))
      .catch(() => setDirectory([])); // no directory capability → local only
  }, []);

  const known = useMemo(() => {
    const map = {};
    for (const p of local) map[p.name.toLowerCase()] = { name: p.name, skillLevel: p.skillLevel, games: p.games, winRate: p.winRate, source: "local" };
    for (const d of directory) {
      const nm = (d.name || "").trim();
      if (!nm) continue;
      const k = nm.toLowerCase();
      const skill = Number((d.profile || {}).self_rated_skill) || null;
      if (map[k]) { map[k].source = "both"; if (skill) map[k].dirSkill = skill; }
      else map[k] = { name: nm, skillLevel: skill || 3, source: "directory", dirSkill: skill };
    }
    return Object.values(map);
  }, [local, directory]);

  const excl = useMemo(() => new Set(excludeNames.map((n) => (n || "").toLowerCase())), [excludeNames]);
  const q = (value || "").trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return [];
    return known.filter((p) => p.name.toLowerCase().includes(q) && !excl.has(p.name.toLowerCase()))
      .sort((a, b) => (b.games || 0) - (a.games || 0) || a.name.localeCompare(b.name))
      .slice(0, 6);
  }, [known, q, excl]);

  const exact = q && known.some((p) => p.name.toLowerCase() === q);
  const showCreate = q.length > 0 && !exact && !excl.has(q);
  const rows = showCreate ? [...matches, { _create: true, name: value.trim() }] : matches;

  useEffect(() => { setHi(0); }, [value]);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (row) => {
    if (!row) return;
    if (row._create) { onCreate?.(row.name); }
    else { onPick?.(row); }
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, rows.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      if (open && rows.length) { e.preventDefault(); choose(rows[hi]); }
      else if (showCreate) { e.preventDefault(); onCreate?.(value.trim()); setOpen(false); }
    } else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-2)" }} />
        <input
          ref={ref} data-testid={`${testid}-input`} value={value} autoFocus={autoFocus}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)} onKeyDown={onKey}
          placeholder={placeholder} autoComplete="off"
          className="w-full rounded-md bg-surface glow-border pl-9 pr-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400"
          style={{ color: "var(--text)" }} />
      </div>
      {open && rows.length > 0 && (
        <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto rounded-xl glass p-1" data-testid={`${testid}-dropdown`}
          style={{ border: "1px solid var(--border)", background: "var(--surface)" }}>
          {rows.map((row, i) => (
            row._create ? (
              <button key="__create" data-testid={`${testid}-create`} onMouseEnter={() => setHi(i)} onClick={() => choose(row)}
                className="flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg text-sm" style={{ background: hi === i ? "var(--bg)" : undefined }}>
                <UserPlus className="w-4 h-4 text-primary-c" /> Create new player “<b>{row.name}</b>”
              </button>
            ) : (
              <button key={row.name} data-testid={`${testid}-opt`} onMouseEnter={() => setHi(i)} onClick={() => choose(row)}
                className="flex items-center justify-between gap-2 w-full text-left px-3 py-2 rounded-lg text-sm" style={{ background: hi === i ? "var(--bg)" : undefined }}>
                <span className="flex items-center gap-2 min-w-0">
                  <span className="truncate font-medium">{row.name}</span>
                  <span className="mono text-[10px] text-primary-c shrink-0">L{row.skillLevel}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0 text-[10px] mono" style={{ color: "var(--text-2)" }}>
                  {row.source !== "directory" && (row.games || 0) > 0 && (
                    <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3" />{row.games}g · {Math.round((row.winRate || 0) * 100)}%</span>
                  )}
                  <span className="uppercase tracking-wider rounded px-1.5 py-0.5" style={{ background: "var(--bg)" }}>
                    {row.source === "directory" ? "Directory" : row.source === "both" ? "Played · Dir" : "Played before"}
                  </span>
                </span>
              </button>
            )
          ))}
        </div>
      )}
    </div>
  );
}
